<!-- eslint-disable vue/no-v-html  -->
<template>
  <div class="chat-msg-container" :class="[message.memberType || '']">
    <!-- 1. Avatar -->
    <div class="avatar-container">
      <div v-if="message.memberType === MemberType.AGENT" class="avatar">
        <img class="visitor-avatar" :src="require('@/assets/img/shared/default/logo.png')" />
      </div>
      <div v-else class="avatar">
        <div class="avatar-layered">
          <img class="avatar_user" :src="require('@/assets/img/user_avatar.png')" />
          <!-- TODO: Avatar Url header is cache-control: no-store -->
          <!-- <img class="avatar-layered__part" src="https://s.sporty.net/common/avatar/23.png" alt=""> -->
        </div>
      </div>
    </div>

    <!-- 2. Message -->
    <div class="chat-msg-wrapper" :class="customize.wrapper">
      <!-- 2-1. Message - File -->
      <template v-if="isMediaMessage">
        <ChatMedia :message="message" />
      </template>
      <div v-else class="chat-msg" :class="customize.chatMsg">
        <!-- Loading/Typing Message -->
        <template v-if="isTypingMessage">
          <div class="typing-indicator">
            <div class="typing-indicator-part" />
            <div class="typing-indicator-part" />
            <div class="typing-indicator-part" />
          </div>
        </template>
        <!-- TODO: move below, placed here for testing/mocked purposes -->
        <AiResponseForUserMessage v-else-if="isAiResponseForUserMessage" :message="message" @send:event="sendEvent" />
        <!-- List option messages -->
        <template v-else-if="isListMessage">
          <span>{{ message.message }}</span>
          <div
            :class="{
              categories: message.type === MessageType.CATEGORIES_LIST,
              tree: message.type === MessageType.HYBRID_LIST || message.type === MessageType.SECTION_LIST,
            }"
          >
            <div
              v-for="(option, index) in message.options"
              :key="`${message.type}-${option.id}`"
              :class="{
                'article-link': isArticleOption(option),
                item: !isArticleOption(option),
                'article-after-item':
                  index > 0 && isArticleOption(option) && !isArticleOption(message.options[index - 1]),
                'selected-item':
                  message.selectedOption &&
                  (option.id === message.selectedOption || option.id === message.selectedOption.id),
              }"
              @click="selectOption(message, option)"
            >
              <!-- Show the icon -->
              <img
                v-if="message.type === MessageType.CATEGORIES_LIST"
                :src="
                  option.iconUri ||
                  'data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAQAAAC1HAwCAAAAC0lEQVR42mNkYAAAAAYAAjCB0C8AAAAASUVORK5CYII='
                "
              />
              <span :class="{ 'break-word': message.type === MessageType.CATEGORIES_LIST }" :title="option.title">{{
                option.title
              }}</span>
            </div>
            <div v-if="showNoneOfTheAbove" class="article-none" @click.stop="selectNoneOfTheAbove(message)">
              {{ $t('chat.none_of_the_above') }}
            </div>
          </div>
        </template>
        <!-- Resolution message -->
        <ResolutionMessage v-else-if="isResolutionMessage" :message="message" @send:event="sendEvent" />
        <!-- Article Message: Preview/Full -->
        <template v-else-if="isArticleMessage">
          <span v-if="message.message">{{ message.message }}</span>
          <div
            :class="[
              'chat-msg__article',
              {
                long: message.type === MessageType.ARTICLE_PREVIEW,
                short: message.type === MessageType.ARTICLE_FULL,
                'mt-1': message.type === MessageType.ARTICLE_PREVIEW && message.message,
              },
            ]"
          >
            <div v-if="message.type === MessageType.ARTICLE_PREVIEW">
              <div class="preview">
                {{ message.article.preview }}
              </div>
              <div class="detail" @click.stop="openArticle(message)">
                <div>{{ $t('chat.article.view_details') }}</div>
              </div>
            </div>
            <div v-else v-html="message.article.body" />
          </div>
        </template>
        <!-- Message: Interactive message  -->
        <template v-else-if="isInteractive">
          <span>{{ interactivePart1 }} </span>
          <span
            :style="{ textDecoration: 'underline', fontWeight: 'bold', cursor: 'pointer' }"
            @click="rollback(message)"
            >{{ interactivePart2 }}</span
          >
        </template>
        <!-- Message: Issues Message -->
        <template v-else-if="isIssuesList">
          <span v-if="message.message">{{ message.message }}</span>
          <div v-for="(issue, index) in message.issues" :key="index" class="issues-wrapper">
            <span>{{ issue }}</span>
          </div>
        </template>
        <AlternativeActionsMessage v-else-if="isAlternativeActionsMessage" :message="message" @send:event="sendEvent" />

        <RequestUserTransactionMessage
          v-else-if="isUserInputRequestForTransaction"
          :message="message"
          @send:event="sendEvent"
        />

        <RequestUserTransactionMessage v-else-if="isUserTransactionSubmissionMessage" :message="message" view-only />

        <ArticleListWithOtherNavigation
          v-else-if="isArticleWithOtherNavigation"
          :message="message"
          @send:event="sendEvent"
        />

        <ArticleCheckFailureMessage
          v-else-if="isArticleCheckFailedMessage"
          :message="message"
          @send:event="sendEvent"
        />

        <PreSupportPrediction v-else-if="isPreSupportPrediction" :message="message" @send:event="sendEvent" />

        <PreSupportConfirmation v-else-if="isPreSupportPrompt.supportOnly" :message="message" @send:event="sendEvent" />

        <PreSupportPromptMessage v-else-if="isPreSupportPrompt.general" :message="message" @send:event="sendEvent" />
        <!-- Other types of messages, only `message` prop is valid -->
        <template v-else>
          <span v-if="messageText.render === 'html'" v-html="messageText.message" />
          <span v-else>{{ messageText.message }}</span>
        </template>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { Component, Prop, Vue } from 'vue-property-decorator';
import ChatMedia from './ChatMedia.vue';
import AlternativeActionsMessage from './AlternativeActionsMessage.vue';
import {
  ArticleExpandedEvent,
  BaseEvent,
  BaseMessage,
  BaseSystemMessage,
  NoneOfTheAboveEvent,
  RollbackEvent,
  SystemArticleMessage,
  SystemOption,
  SystemOptionEvent,
} from '@/common/bot/types';
import { ItemType, getInteractivePart, OptionUtils } from '@/common/bot/lib';
import { MessageType, SystemEvent, MemberType } from '@/common/bot/enum';
import { identifyUrls, sanitizeHTML } from '@/common/utils';
import ResolutionMessage from './Resolution/ResolutionMessage.vue';
import ArticleListWithOtherNavigation from './ArticleListWithOtherNavigation.vue';
import ArticleCheckFailureMessage from './ArticleCheckFailureMessage.vue';
import PreSupportConfirmation from './PreSupportConfirmation.vue';
import PreSupportPrediction from './PreSupportPrediction.vue';
import PreSupportPromptMessage from './PreSupportPromptMessage.vue';
import RequestUserTransactionMessage from './RequestUserTransactionMessage.vue';
import AiResponseForUserMessage from './AiResponseForUserMessage.vue';
import { removeMessagePrefix } from '@/common/bot/flow-frameworks/messages';

@Component({
  components: {
    ChatMedia,
    ResolutionMessage,
    AlternativeActionsMessage,
    ArticleListWithOtherNavigation,
    ArticleCheckFailureMessage,
    PreSupportConfirmation,
    PreSupportPromptMessage,
    RequestUserTransactionMessage,
    PreSupportPrediction,
    AiResponseForUserMessage,
  },
})
export default class ChatMessageSwitch extends Vue {
  @Prop({ default: '' }) readonly addClass!: string;
  @Prop({ default: () => ({}) }) readonly message!: BaseMessage;

  readonly MessageType = MessageType;
  readonly MemberType = MemberType;

  get isArticleWithOtherNavigation() {
    return this.message.type === MessageType.ARTICLE_LIST_WITH_OTHER_NAVIGATION;
  }

  get isPreSupportPrompt() {
    if (!ItemType.isPreSupportPromptMessage(this.message)) return {};
    const { type: messageType } = this.message;
    if (MessageType.PRE_SUPPORT_PROMPT_SUPPORT_ONLY === messageType) return { supportOnly: true };
    return { general: true };
  }

  get isPreSupportPrediction() {
    return ItemType.isPreSupportPredictionMessage(this.message);
  }

  get isArticleCheckFailedMessage() {
    return ItemType.isArticleCheckFailureMessage(this.message);
  }

  get isInteractive() {
    return ItemType.isInteractiveMessage(this.message);
  }

  get interactivePart1() {
    return getInteractivePart(this.message, 0);
  }

  get interactivePart2() {
    return getInteractivePart(this.message, 1);
  }

  get isListMessage() {
    return ItemType.isListMessage(this.message);
  }

  get isTypingMessage() {
    return ItemType.isTypingMessage(this.message);
  }

  get isResolutionMessage() {
    return ItemType.isResolutionMessage(this.message);
  }

  get showNoneOfTheAbove() {
    return ItemType.isListMessage(this.message) && this.message.config?.noneOfTheAbove?.show;
  }

  get isArticleMessage() {
    return ItemType.isArticleMessage(this.message);
  }

  get isChatMessage() {
    return ItemType.isChatMessage(this.message);
  }

  get isIssuesList() {
    return ItemType.isTransactionMessage(this.message);
  }

  get isSystemInteractionMessage() {
    return this.message.type === MessageType.RESOLUTION || this.isListMessage;
  }

  get isMediaMessage() {
    return ItemType.isMediaMessage(this.message);
  }

  get isAlternativeActionsMessage() {
    return ItemType.isAlternativeActionsMessage(this.message);
  }

  get isUserInputRequestForTransaction() {
    return ItemType.isUserInputRequestTransactionMessage(this.message);
  }

  get isUserTransactionSubmissionMessage() {
    return ItemType.isUserTransactionSubmissionMessage(this.message);
  }

  get isAiResponseForUserMessage() {
    return ItemType.isAiMessageForUser(this.message);
  }

  get customize() {
    // Apply classes to the wrappers based on
    // the message type
    return {
      wrapper: {
        'max-w-unset': this.isUserInputRequestForTransaction || this.isUserTransactionSubmissionMessage,
      },
      chatMsg: {
        'max-w-unset': this.isUserInputRequestForTransaction || this.isUserTransactionSubmissionMessage,
        'large-mb': this.isAiResponseForUserMessage,
      },
    };
  }

  get messageText() {
    const { message } = this;
    const isAgentSideChatMessage = message.type === MessageType.CHAT_MESSAGE && message.memberType === MemberType.AGENT;
    const messageWithoutPrefix = removeMessagePrefix(message.message);

    const containsHtml = /<[^>]+>/.test(messageWithoutPrefix);

    if (containsHtml) {
      return {
        render: 'html',
        message: sanitizeHTML(messageWithoutPrefix),
      };
    }

    if (!isAgentSideChatMessage) {
      return {
        render: 'string',
        message: messageWithoutPrefix,
      };
    }

    const messageWithUrls = identifyUrls(messageWithoutPrefix);
    if (messageWithUrls === messageWithoutPrefix) {
      return {
        render: 'string',
        message: messageWithoutPrefix,
      };
    }
    return {
      render: 'html',
      message: messageWithUrls,
    };
  }

  isArticleOption(option: SystemOption) {
    return OptionUtils.isArticleOption(option);
  }

  isSectionOption(option: SystemOption) {
    return OptionUtils.isSectionOption(option);
  }

  sendEvent($event: BaseEvent) {
    this.$emit('input', $event);
  }

  selectOption(message: BaseSystemMessage, selected: SystemOption) {
    const $event: SystemOptionEvent = {
      message,
      type: SystemEvent.LIST_OPTION_SELECTED,
      payload: { selected },
    };
    this.sendEvent($event);
  }

  selectNoneOfTheAbove(message: BaseSystemMessage) {
    const $event: NoneOfTheAboveEvent = {
      message,
      type: SystemEvent.NONE_OF_THE_ABOVE_SELECTED,
    };
    this.sendEvent($event);
  }

  rollback(message: BaseSystemMessage) {
    const $event: RollbackEvent = {
      message,
      type: SystemEvent.USER_ROLLBACK,
    };
    this.sendEvent($event);
  }

  openArticle(message: SystemArticleMessage) {
    const $event: ArticleExpandedEvent = {
      message,
      type: SystemEvent.ARTICLE_PREVIEW_EXPANDED,
    };
    this.sendEvent($event);
  }
}
</script>

<style lang="scss" scoped>
.max-w-unset.max-w-unset {
  max-width: unset;
}

.large-mb.large-mb {
  margin-bottom: 1.6rem;
}
</style>
